import { PERMISSIONS } from "@/constants/permissions"
import { useGuardedMutation } from "@/hooks/useGuardedMutation"
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { apiClientV2 } from "../../../services/api/apiClient"
import {
  invalidateMcEntityCaches,
  mcCursorList,
  mcCursorListKeepPrevious,
} from "./mcQueryHelpers"
import type {
  ArticleRow,
  ComplianceEventOut,
  SubstanceCreateIn,
  SubstanceOut,
} from "./types/masterData"

const BASE = "/material-compliance/master-data/substances"

// ─── List ─────────────────────────────────────────────────────────────────────

export function useSubstanceList(
  params: {
    q?: string
    article_id?: string
    supplier_id?: string
    product_id?: string
    sort?: string
    limit?: number
    /** Set false to skip the request entirely, e.g. while a sibling query owns the active view. Defaults to true. */
    enabled?: boolean
  } = {},
) {
  const { enabled = true, ...listParams } = params
  return useInfiniteQuery(
    mcCursorListKeepPrevious<SubstanceOut>(
      ["mc", "substances", "list", listParams],
      BASE,
      listParams,
      enabled,
    ),
  )
}

// The FULL universal registry (unscoped by org usage) — the source the
// declaration-review substance picker needs, so a reviewer can connect any known
// substance, not just ones already on the org's articles. Paginated; consumers
// that need the whole list must follow fetchNextPage until hasNextPage is false.
export function useGlobalSubstanceList(
  params: {
    q?: string
    limit?: number
    /** Set false to skip the request entirely, e.g. while a sibling query owns the active view. Defaults to true. */
    enabled?: boolean
  } = {},
) {
  const { enabled = true, ...listParams } = params
  return useInfiniteQuery(
    mcCursorListKeepPrevious<SubstanceOut>(
      ["mc", "substances", "global", "list", listParams],
      `${BASE}/global`,
      listParams,
      enabled,
    ),
  )
}

// ─── Detail (by substance.id) ────────────────────────────────────────────────

export function useSubstanceDetail(substanceId: string | null) {
  return useQuery({
    queryKey: ["mc", "substances", "detail", substanceId],
    queryFn: () =>
      apiClientV2
        .get<SubstanceOut>(`${BASE}/${substanceId}`)
        .then((r) => r.data),
    enabled: !!substanceId,
  })
}

// ─── Global detail (no org-usage check) — use for declaration review ─────────

export function useGlobalSubstanceDetail(substanceId: string | null) {
  return useQuery({
    queryKey: ["mc", "substances", "global", "detail", substanceId],
    queryFn: () =>
      apiClientV2
        .get<SubstanceOut>(`${BASE}/global/${substanceId}`)
        .then((r) => r.data),
    enabled: !!substanceId,
  })
}

// ─── Sub-resource: articles using this substance ──────────────────────────────

export function useSubstanceArticles(
  substanceId: string | null,
  params: { limit?: number } = {},
) {
  return useInfiniteQuery(
    mcCursorList<ArticleRow>(
      ["mc", "substances", substanceId, "articles", params],
      `${BASE}/${substanceId}/articles`,
      params,
      !!substanceId,
    ),
  )
}

// ─── Sub-resource: audit trail ────────────────────────────────────────────────

export function useSubstanceAudit(
  substanceId: string | null,
  params: { limit?: number } = {},
) {
  return useInfiniteQuery(
    mcCursorList<ComplianceEventOut>(
      ["mc", "substances", substanceId, "audit", params],
      `${BASE}/${substanceId}/audit`,
      params,
      !!substanceId,
    ),
  )
}

// ─── Create ───────────────────────────────────────────────────────────────────

/**
 * POST /master-data/substances's 409/422 `detail`, as it ACTUALLY arrives —
 * snake_case, because apiClientV2's response interceptor only camelCases the
 * success path (mirrors `CreateSupplierErrorDetail` in suppliersApi.ts, the
 * identical convention for that endpoint's own 409). Two shapes share this
 * type: a 409 SUBSTANCE_ALREADY_EXISTS, where `substance_id` names the
 * EXISTING substance that already holds the requested identifier — the
 * caller's "use this existing one instead" escape hatch — and a 422
 * IDENTIFIER_REQUIRED / INVALID_CAS_NUMBER, which carries no id at all.
 */
export type CreateSubstanceErrorCode =
  | "SUBSTANCE_ALREADY_EXISTS"
  | "IDENTIFIER_REQUIRED"
  | "INVALID_CAS_NUMBER"
  | string

export interface CreateSubstanceErrorDetail {
  code: CreateSubstanceErrorCode
  message?: string
  substance_id?: string
}

/** Reads the 409/422's `detail` off an axios-shaped error, or null when the
 *  error carries none (a network failure, a 5xx already toasted by the global
 *  interceptor, …) — mirrors `createSupplierErrorDetailOf` in suppliersApi.ts. */
export function createSubstanceErrorDetailOf(
  error: unknown,
): CreateSubstanceErrorDetail | null {
  const detail = (
    error as { response?: { data?: { detail?: CreateSubstanceErrorDetail } } }
  )?.response?.data?.detail
  return detail?.code ? detail : null
}

/**
 * Creates a master-data substance. NO onError toast here, deliberately, for
 * the same reason as `useCreateSupplier`: a >=500 is already toasted once by
 * the global interceptor, and this endpoint's one business error (409
 * SUBSTANCE_ALREADY_EXISTS) needs `detail.substance_id` rendered inline by
 * the caller's own "use this existing one instead" affordance — a toast alone
 * would lose that id the moment it faded.
 */
export function useCreateSubstance() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: async (input: SubstanceCreateIn): Promise<SubstanceOut> =>
      apiClientV2.post<SubstanceOut>(BASE, input).then((r) => r.data),
    onSuccess: (created) => {
      // Two independent list caches read this entity: the org-scoped
      // ["mc","substances","list",...] (dropped by invalidateMcEntityCaches
      // below) and the unscoped catalog ["mc","substances","global","list",...]
      // that useGlobalSubstanceList feeds the declaration-review substance
      // picker from. Both need dropping so a freshly-created substance shows
      // up wherever either list is open.
      invalidateMcEntityCaches(queryClient, "substances", created.id)
      queryClient.invalidateQueries({
        queryKey: ["mc", "substances", "global"],
      })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_MASTER_DATA_EDIT)
}
